/* ═══════════════════════════════════════════════════════════════
   The Designer — the Issuance Desk.

   Set-level review, the Procore-drawings model: a manual-level issuance
   (IFC, Addendum 1, ASI 3…) freezes per-section snapshots and records a
   manifest of what shipped and what changed. From here you issue the
   manual, read the timeline, open any past issuance exactly as it
   existed, export it, and read the addendum change summary the diff
   computed.

   §2.10 (Josh, 2026-08-12): **issuing also produces the files.** An
   issuance is a sealed set going out the door, so the flow that records
   it hands you the documents too, with the same choices Export offers.
   Export on its own still exists and still just makes files — the two
   buttons mean different things and both stay.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { ProjectManualIssuance, ProjectSpec } from '../../shared/types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import EmptyState from '../components/EmptyState';
import { isWeb } from '../lib/platform';
import type { ExportFormat } from './exportManual';
import {
  changedSince,
  computeAddendumSummary,
  latestManualIssuance,
  resolveBaselineIssuance,
  type SectionChangeSummary,
} from './issuance';
import { resolveFormaForSpec } from './useDesignerDoc';
import type { DesignerExportContext } from './useDesignerDoc';

const DeskIcon = (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 8v4l3 3" />
    <circle cx="12" cy="12" r="9" />
  </svg>
);

function fmtDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
}

/** One section row inside the issue flow's manifest picker. */
function ManifestRow({
  spec,
  checked,
  changedLabel,
  onToggle,
}: {
  spec: ProjectSpec;
  checked: boolean;
  changedLabel: string | null;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      className="dsg-desk-manifest-row"
      onClick={onToggle}
    >
      <span className={`dsg-rsp-box${checked ? ' is-on' : ''}`} aria-hidden="true">
        {checked && (
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        )}
      </span>
      <span className="dsg-desk-manifest-csi">{spec.csiNumber || '—'}</span>
      <span className="dsg-desk-manifest-title">{spec.title || 'Untitled section'}</span>
      {changedLabel && <span className="dsg-chip dsg-chip-edited">{changedLabel}</span>}
    </button>
  );
}

function ChangeSummaryList({ summary }: { summary: SectionChangeSummary[] }) {
  if (!summary.length) {
    return <div className="dsg-desk-summary-none">No sections in this issuance.</div>;
  }
  return (
    <div className="dsg-desk-summary">
      {summary.map((s) => (
        <div key={s.specId} className="dsg-desk-summary-section">
          <div className="dsg-desk-summary-head">
            <span className="dsg-desk-manifest-csi">{s.csiNumber || '—'}</span>
            <span className="dsg-desk-manifest-title">{s.title}</span>
            {s.newSection ? (
              <span className="dsg-chip dsg-chip-edited">New section</span>
            ) : s.changes.length === 0 ? (
              <span className="dsg-chip dsg-chip-clean">Unchanged</span>
            ) : (
              <span className="dsg-chip dsg-chip-edited">
                {s.changes.length} change{s.changes.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {s.changes.length > 0 && (
            <ul className="dsg-desk-summary-changes">
              {s.changes.map((c, i) => (
                <li key={i} className={`dsg-desk-change dsg-desk-change-${c.kind}`}>
                  <span className="dsg-desk-change-kind">
                    {c.kind === 'added' ? 'Added' : c.kind === 'removed' ? 'Deleted' : 'Revised'}
                  </span>
                  <span className="dsg-desk-change-ref">{c.ref || '¶'}</span>
                  <span className="dsg-desk-change-excerpt">{c.excerpt}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

/** The file half of an issue (§2.10) — the same choices Export offers. */
export interface IssueExportOptions {
  format: ExportFormat;
  /** Show what changed since the previous issuance. Only offered when there
   *  IS a previous one; before the first seal there is nothing to mark. */
  marked: boolean;
}

function Seg({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ key: string; label: string }>;
  value: string;
  onChange: (key: string) => void;
  label: string;
}) {
  return (
    <div className="dsg-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          className={`dsg-seg-btn${value === o.key ? ' is-active' : ''}`}
          aria-pressed={value === o.key}
          onClick={() => onChange(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export interface IssuanceDeskProps {
  open: boolean;
  onClose: () => void;
  projectName: string;
  /** Every spec in the project, CSI order (the Manual). */
  specs: ProjectSpec[];
  manualIssuances: ProjectManualIssuance[];
  exportCtx: DesignerExportContext | null;
  canIssue: boolean;
  busy: boolean;
  /** Freeze + record + EXPORT (§2.10). */
  onIssue: (
    meta: { name: string; date?: string; notes?: string },
    sectionIds: string[],
    exportOptions: IssueExportOptions
  ) => Promise<void>;
  /** Load a past issuance read-only onto the canvas. */
  onViewIssuance: (mi: ProjectManualIssuance) => void;
  /** Export a past issuance as it existed (docx via the shared pipeline). */
  onExportIssuance: (mi: ProjectManualIssuance) => void;
  toast: (msg: string, isError?: boolean) => void;
}

export function IssuanceDesk({
  open,
  onClose,
  projectName,
  specs,
  manualIssuances,
  exportCtx,
  canIssue,
  busy,
  onIssue,
  onViewIssuance,
  onExportIssuance,
  toast,
}: IssuanceDeskProps) {
  const ordered = React.useMemo(
    () => [...manualIssuances].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')),
    [manualIssuances]
  );
  const latest = latestManualIssuance(manualIssuances);

  // ── Issue flow state ──
  const [issuing, setIssuing] = React.useState(false);
  const [name, setName] = React.useState('');
  const [date, setDate] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = React.useState('');
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = React.useState(false);
  // The file half of the issue (§2.10).
  const [format, setFormat] = React.useState<ExportFormat>('docx');
  const [marked, setMarked] = React.useState(false);

  // ── Summary expansion ──
  const [openSummaryId, setOpenSummaryId] = React.useState<string | null>(null);

  const startIssue = () => {
    setName(manualIssuances.length === 0 ? 'IFC' : `Addendum ${manualIssuances.length}`);
    setDate(new Date().toISOString().slice(0, 10));
    setNotes('');
    setSelected(new Set(specs.map((s) => s.id))); // default: ALL sections
    setFormat('docx');
    // An addendum normally goes out marked; a first seal has nothing to mark.
    setMarked(!!latest);
    setIssuing(true);
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = async () => {
    if (!name.trim()) {
      toast('Name the issuance — “IFC”, “Addendum 1”…', true);
      return;
    }
    if (selected.size === 0) {
      toast('Pick at least one section to issue.', true);
      return;
    }
    setSubmitting(true);
    try {
      await onIssue({ name: name.trim(), date, notes: notes.trim() || undefined }, [...selected], {
        format,
        marked: marked && !!latest,
      });
      setIssuing(false);
    } catch (err) {
      toast(
        'Issue failed: ' + (err instanceof Error ? err.message : String(err ?? 'unknown error')),
        true
      );
    } finally {
      setSubmitting(false);
    }
  };

  const summaryFor = (mi: ProjectManualIssuance): SectionChangeSummary[] => {
    const idx = ordered.findIndex((m) => m.id === mi.id);
    const previous = idx >= 0 && idx + 1 < ordered.length ? ordered[idx + 1] : null;
    return computeAddendumSummary(mi, previous, specs, (s) =>
      resolveFormaForSpec(exportCtx?.formas || [], s, s.projectId)
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl dsg-desk" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Issuance Desk</DialogTitle>
          <div className="dsg-desk-subtitle">
            {projectName} · {specs.length} section{specs.length === 1 ? '' : 's'}
            {latest ? ` · current baseline: ${latest.name}` : ' · design phase — not yet issued'}
          </div>
        </DialogHeader>

        {issuing ? (
          <div className="dsg-desk-issue">
            <div className="dsg-desk-fields">
              <label className="dsg-desk-field">
                <span className="dsg-desk-label">Issuance name</span>
                <input
                  className="dsg-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Addendum 1"
                  autoFocus
                />
              </label>
              <label className="dsg-desk-field dsg-desk-field-date">
                <span className="dsg-desk-label">Date</span>
                <input
                  className="dsg-input"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </label>
            </div>
            <label className="dsg-desk-field">
              <span className="dsg-desk-label">Notes</span>
              <textarea
                className="dsg-input dsg-desk-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="What this issuance covers (optional)"
              />
            </label>

            <div className="dsg-desk-label">
              Sections to include — {selected.size} of {specs.length}
            </div>
            <div className="dsg-desk-manifest" role="group" aria-label="Sections to include">
              {specs.map((s) => {
                const baseline = resolveBaselineIssuance(s, latest);
                const changedLabel = !latest
                  ? null
                  : !baseline
                    ? 'New since ' + latest.name
                    : changedSince(s, baseline)
                      ? 'Edited since ' + latest.name
                      : null;
                return (
                  <ManifestRow
                    key={s.id}
                    spec={s}
                    checked={selected.has(s.id)}
                    changedLabel={changedLabel}
                    onToggle={() => toggle(s.id)}
                  />
                );
              })}
            </div>

            {/* ── The files that go out with it (§2.10) ── */}
            <div className="dsg-desk-field">
              <span className="dsg-desk-label">File format</span>
              {isWeb ? (
                <span className="dsg-export-hint">
                  Word (.docx) — PDF uses your installed fonts and runs in the desktop app.
                </span>
              ) : (
                <Seg
                  label="File format"
                  value={format}
                  onChange={(k) => setFormat(k as ExportFormat)}
                  options={[
                    { key: 'docx', label: 'Word' },
                    { key: 'pdf', label: 'PDF' },
                    { key: 'both', label: 'Both' },
                  ]}
                />
              )}
            </div>

            {latest && (
              <div className="dsg-desk-field">
                <span className="dsg-desk-label">Changes</span>
                <Seg
                  label="Clean or marked"
                  value={marked ? 'marked' : 'clean'}
                  onChange={(k) => setMarked(k === 'marked')}
                  options={[
                    { key: 'clean', label: 'Clean' },
                    { key: 'marked', label: `Mark changes since ${latest.name}` },
                  ]}
                />
              </div>
            )}

            <div className="dsg-desk-actions">
              <span className="dsg-desk-actions-note">
                {latest
                  ? 'Records the revision and saves the files.'
                  : 'Seals this set as the baseline and saves the files. Everything you change afterwards shows as a revision.'}
              </span>
              <button
                type="button"
                className="dsg-btn"
                onClick={() => setIssuing(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dsg-primary"
                onClick={() => void submit()}
                disabled={submitting || busy}
              >
                {submitting
                  ? 'Issuing…'
                  : `Issue and save ${selected.size} section${selected.size === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        ) : ordered.length === 0 ? (
          <EmptyState
            icon={DeskIcon}
            title="Nothing has been issued yet"
            subtitle="Issuing seals the set — the 100% / IFC / IFB milestone that goes out the door. It records what shipped, saves the files, and from then on every change shows as a revision. Until you issue, you are still in design and edits stay quiet."
            {...(canIssue ? { action: { label: 'Issue this set…', onClick: startIssue } } : {})}
          />
        ) : (
          <div className="dsg-desk-timeline" role="list" aria-label="Issuance timeline">
            {ordered.map((mi, i) => {
              const changed = mi.manifest.filter((m) => m.changed).length;
              const isFirst = i === ordered.length - 1;
              return (
                <div key={mi.id} role="listitem" className="dsg-desk-issuance">
                  <div className="dsg-desk-issuance-head">
                    <span className="dsg-desk-issuance-name">{mi.name}</span>
                    <span className="dsg-desk-issuance-meta">
                      {fmtDate(mi.date || mi.createdAt)}
                      {mi.issuedBy ? ` · ${mi.issuedBy}` : ''}
                      {' · '}
                      {mi.manifest.length} section{mi.manifest.length === 1 ? '' : 's'}
                      {isFirst ? ' · initial issue' : ` · ${changed} changed`}
                    </span>
                    {i === 0 && <span className="dsg-chip dsg-chip-clean">Baseline</span>}
                  </div>
                  {mi.notes && <div className="dsg-desk-issuance-notes">{mi.notes}</div>}
                  <div className="dsg-desk-issuance-actions">
                    <button type="button" className="dsg-btn" onClick={() => onViewIssuance(mi)}>
                      View as issued
                    </button>
                    <button type="button" className="dsg-btn" onClick={() => onExportIssuance(mi)}>
                      Export…
                    </button>
                    <button
                      type="button"
                      className="dsg-btn"
                      aria-expanded={openSummaryId === mi.id}
                      onClick={() => setOpenSummaryId(openSummaryId === mi.id ? null : mi.id)}
                    >
                      {openSummaryId === mi.id ? 'Hide change summary' : 'Change summary'}
                    </button>
                  </div>
                  {openSummaryId === mi.id &&
                    (isFirst ? (
                      <div className="dsg-desk-summary-none">
                        Initial issue — every section entered the record here; there is no earlier
                        issuance to compare against.
                      </div>
                    ) : (
                      <ChangeSummaryList summary={summaryFor(mi)} />
                    ))}
                </div>
              );
            })}
          </div>
        )}

        {!issuing && ordered.length > 0 && (
          <div className="dsg-desk-actions">
            <button
              type="button"
              className="dsg-primary"
              onClick={startIssue}
              disabled={!canIssue || busy}
              title={canIssue ? undefined : 'You need the create-issuance permission'}
            >
              Issue again…
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
